import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface JournalVoucherFilters {
  limit?: number;
  offset?: number;
  transaction_no?: string;
  posting_date?: string;
  status?: string;
  is_deleted?: boolean | number;
}

export interface JournalVoucherApiResponse {
  data: {
    journal_vouchers: any[];
    total: number;
    limit: number;
    offset: number;
  };
  message?: string;
  status?: string;
}

export interface JournalVoucherSingleResponse {
  data: any;
  message?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class JournalVoucherService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all journal vouchers with optional filters
   * Endpoint: GET /api/v1/journal-vouchers
   * Query Parameters: limit, offset, transaction_no, posting_date, status, is_deleted
   */
  getJournalVouchers(filters?: JournalVoucherFilters): Observable<JournalVoucherApiResponse> {
    let params = new HttpParams();

    if (filters) {
      // Add pagination parameters
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }
      if (filters.offset !== undefined) {
        params = params.set('offset', filters.offset.toString());
      }

      // Add filter parameters
      if (filters.transaction_no) {
        params = params.set('transaction_no', filters.transaction_no);
      }
      if (filters.posting_date) {
        params = params.set('posting_date', filters.posting_date);
      }
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.is_deleted !== undefined) {
        params = params.set('is_deleted', typeof filters.is_deleted === 'boolean' ? (filters.is_deleted ? '1' : '0') : filters.is_deleted.toString());
      }
    }

    return this.http.get<JournalVoucherApiResponse>(`${this.API_BASE_URL}/api/v1/journal-vouchers/`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get single journal voucher by ID
   * Endpoint: GET /api/v1/journal-vouchers/{id}
   */
  getJournalVoucherById(id: string | number): Observable<JournalVoucherSingleResponse> {
    return this.http.get<JournalVoucherSingleResponse>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}`, {
      withCredentials: true
    });
  }

  /**
   * Create a new journal voucher
   * Endpoint: POST /api/v1/journal-vouchers
   */
  createJournalVoucher(journalVoucherData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/`, journalVoucherData, {
      withCredentials: true
    });
  }

  /**
   * Update an existing journal voucher
   * Endpoint: PUT /api/v1/journal-vouchers/{id}
   */
  updateJournalVoucher(id: string | number, journalVoucherData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}`, journalVoucherData, {
      withCredentials: true
    });
  }

  /**
   * Delete a journal voucher (soft delete)
   * Endpoint: DELETE /api/v1/journal-vouchers/{id}
   */
  deleteJournalVoucher(id: string | number): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}`, {
      withCredentials: true
    });
  }

  /**
   * Post a draft journal voucher
   * Endpoint: POST /api/v1/journal-vouchers/{id}/post
   */
  postJournalVoucher(id: string | number): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}/post`, {}, {
      withCredentials: true
    });
  }

  /**
   * Void a posted journal voucher
   * Endpoint: POST /api/v1/journal-vouchers/{id}/void
   */
  voidJournalVoucher(id: string | number, reason: string): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/api/v1/journal-vouchers/${id}/void`, { reason }, {
      withCredentials: true
    });
  }

  /**
   * Get all form templates
   * Endpoint: GET /api/v1/journal-voucher-form-templates/
   */
  getFormTemplates(): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get default form template
   * Endpoint: GET /api/v1/journal-voucher-form-templates/default
   */
  getDefaultFormTemplate(): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/default`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get form template by ID
   * Endpoint: GET /api/v1/journal-voucher-form-templates/{id}
   */
  getFormTemplateById(templateId: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/${templateId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Get form template by ID with custom fields
   * Endpoint: GET /api/v1/journal-voucher-form-templates/{id}/with-custom-fields
   */
  getFormTemplateByIdWithCustomFields(templateId: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-voucher-form-templates/${templateId}/with-custom-fields`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  /**
   * Create client invoice mappings for a JV line
   * Endpoint: POST /api/v1/journal-vouchers/{id}/lines/{line_id}/client-invoices
   */
  createJVLineClientInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/client-invoices`;
    return this.http.post<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Update client invoice mappings for a JV line
   * Endpoint: PUT /api/v1/journal-vouchers/{id}/lines/{line_id}/client-invoices
   */
  updateJVLineClientInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/client-invoices`;
    return this.http.put<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Create vendor invoice mappings for a JV line
   * Endpoint: POST /api/v1/journal-vouchers/{id}/lines/{line_id}/vendor-invoices
   */
  createJVLineVendorInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/vendor-invoices`;
    return this.http.post<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Update vendor invoice mappings for a JV line
   * Endpoint: PUT /api/v1/journal-vouchers/{id}/lines/{line_id}/vendor-invoices
   */
  updateJVLineVendorInvoiceMappings(jvId: string | number, lineId: string | number, mappings: any): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}/vendor-invoices`;
    return this.http.put<any>(url, mappings, { withCredentials: true });
  }

  /**
   * Get journal voucher line item by ID
   * Endpoint: GET /api/v1/journal-vouchers/{id}/lines/{line_id}
   */
  getJournalVoucherLineById(jvId: string | number, lineId: string | number): Observable<any> {
    const url = `${this.API_BASE_URL}/api/v1/journal-vouchers/${jvId}/lines/${lineId}`;
    return this.http.get<any>(url, { withCredentials: true });
  }
}

